import { useState } from 'react';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TranslateIcon from '@mui/icons-material/Translate';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import type { TranslationLanguage, UserDocType } from '@ast-data/shared';
import { formatDate } from '../../utils/formatDate';
import { useDocs } from '../media/useOwnerMedia';
import { useUserLabel } from '../users/useUserLabel';
import type { FieldTranslation } from './useFieldTranslation';

export type TranslationView = 'original' | 'translation';

/**
 * The translate button. When the text is plainly German or English it
 * translates into the other language straight away; when its language cannot
 * be told (a few words), it asks for the direction.
 */
export function TranslateMenuButton({
    fieldTranslation,
    onTranslated,
    disabled = false
}: {
    fieldTranslation: FieldTranslation;
    onTranslated: () => void;
    /** Off for the moment — while a dictation still writes the text. */
    disabled?: boolean;
}) {
    const intl = useIntl();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const { blocker, busy, targets } = fieldTranslation;
    // One sensible direction: the button does it. Two: it asks which.
    const onlyTarget = targets.length === 1 ? targets[0] : null;

    async function choose(targetLang: TranslationLanguage) {
        setAnchor(null);
        if (await fieldTranslation.translate(targetLang)) {
            onTranslated();
        }
    }

    let title: string;
    if (blocker && blocker !== 'busy') {
        title = intl.formatMessage({ id: `translation.blocker.${blocker}` });
    } else if (busy) {
        title = intl.formatMessage({ id: 'translation.translating' });
    } else if (onlyTarget) {
        title = intl.formatMessage({
            id: `translation.translateTo.${onlyTarget}`
        });
    } else {
        title = intl.formatMessage({ id: 'translation.translate' });
    }

    return (
        <>
            {/* A span, so the tooltip still explains a disabled button. */}
            <Tooltip title={title}>
                <span>
                    <IconButton
                        size="small"
                        aria-label={intl.formatMessage({
                            id: 'translation.translate'
                        })}
                        disabled={disabled || blocker !== null}
                        onClick={(event) =>
                            onlyTarget
                                ? void choose(onlyTarget)
                                : setAnchor(event.currentTarget)
                        }
                    >
                        {busy ? (
                            <CircularProgress size={18} />
                        ) : (
                            <TranslateIcon fontSize="small" />
                        )}
                    </IconButton>
                </span>
            </Tooltip>
            <Menu
                anchorEl={anchor}
                open={anchor !== null}
                onClose={() => setAnchor(null)}
            >
                {targets.map((targetLang) => (
                    <MenuItem
                        key={targetLang}
                        onClick={() => void choose(targetLang)}
                    >
                        {intl.formatMessage({
                            id: `translation.translateTo.${targetLang}`
                        })}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

/** Original | Übersetzung, each labelled with its language. */
export function TranslationViewToggle({
    fieldTranslation,
    view,
    onChange,
    disabled = false
}: {
    fieldTranslation: FieldTranslation;
    view: TranslationView;
    onChange: (view: TranslationView) => void;
    /** Off for the moment — while a dictation still writes the text. */
    disabled?: boolean;
}) {
    const intl = useIntl();
    const translation = fieldTranslation.translation;
    if (!translation) {
        return null;
    }
    // Our two by their own label; any other source DeepL named, by the
    // browser's name for it ("Polnisch", "Polish").
    const languageName = (language: string) =>
        language === 'de' || language === 'en'
            ? intl.formatMessage({ id: `translation.language.${language}` })
            : (new Intl.DisplayNames([intl.locale], { type: 'language' }).of(
                  language
              ) ?? language.toUpperCase());

    return (
        <ToggleButtonGroup
            size="small"
            exclusive
            disabled={disabled}
            value={view}
            onChange={(_event, next: TranslationView | null) => {
                if (next) {
                    onChange(next);
                }
            }}
            aria-label={intl.formatMessage({ id: 'translation.view.label' })}
            sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1 } }}
        >
            {/* Plain `title`s: the group styles its DIRECT children, which a
                Tooltip wrapper would no longer be. */}
            <ToggleButton
                value="original"
                title={intl.formatMessage(
                    { id: 'translation.view.originalHint' },
                    { language: languageName(translation.sourceLang) }
                )}
            >
                {translation.sourceLang.toUpperCase()}
            </ToggleButton>
            <ToggleButton
                value="translation"
                title={intl.formatMessage(
                    { id: 'translation.view.translationHint' },
                    { language: languageName(translation.targetLang) }
                )}
            >
                {translation.targetLang.toUpperCase()}
            </ToggleButton>
        </ToggleButtonGroup>
    );
}

function ApproverName({ userId }: { userId: string }) {
    const users = useDocs<UserDocType>('users', { id: userId });
    const label = useUserLabel();
    return <>{users[0] ? label(users[0]) : userId}</>;
}

const STATUS_COLOR = {
    machine: 'warning.main',
    edited: 'info.main',
    approved: 'success.main',
    stale: 'error.main'
} as const;

/**
 * The line under the header while the translation is shown: what it is
 * (always visible — it is the machine-translation mark) and what can be done
 * with it.
 */
export function TranslationStatusBar({
    fieldTranslation
}: {
    fieldTranslation: FieldTranslation;
}) {
    const intl = useIntl();
    const { translation, status, canReview, blocker } = fieldTranslation;
    if (!translation || !status) {
        return null;
    }

    const statusText =
        status === 'approved'
            ? intl.formatMessage(
                  { id: 'translation.status.approved' },
                  {
                      name: <ApproverName userId={translation.approvedBy} />,
                      date: formatDate(translation.approvedAt)
                  }
              )
            : intl.formatMessage({ id: `translation.status.${status}` });

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                px: 1.5,
                py: 0.5,
                borderBottom: 1,
                borderColor: 'divider'
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    mr: 'auto',
                    fontWeight: 500,
                    color: STATUS_COLOR[status]
                }}
            >
                {statusText}
            </Typography>
            {status === 'stale' && (
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    disabled={blocker !== null}
                    onClick={() =>
                        void fieldTranslation.translate(
                            translation.targetLang as TranslationLanguage
                        )
                    }
                >
                    {intl.formatMessage({ id: 'translation.retranslate' })}
                </Button>
            )}
            {canReview && (status === 'machine' || status === 'edited') && (
                // describeChild: the hint describes the button, its name
                // stays "Abnehmen".
                <Tooltip
                    describeChild
                    title={intl.formatMessage({
                        id: 'translation.approveHint'
                    })}
                >
                    <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<TaskAltIcon />}
                        onClick={fieldTranslation.approve}
                    >
                        {intl.formatMessage({ id: 'translation.approve' })}
                    </Button>
                </Tooltip>
            )}
            {canReview && (
                <Tooltip
                    title={intl.formatMessage({ id: 'translation.discard' })}
                >
                    <IconButton
                        size="small"
                        aria-label={intl.formatMessage({
                            id: 'translation.discard'
                        })}
                        onClick={() => void fieldTranslation.discard()}
                    >
                        <DeleteOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
}

/** Why the last translate did not come back, under the header. */
export function TranslationErrorLine({
    fieldTranslation
}: {
    fieldTranslation: FieldTranslation;
}) {
    const intl = useIntl();
    if (!fieldTranslation.error) {
        return null;
    }
    return (
        <Typography
            variant="caption"
            role="alert"
            sx={{
                display: 'block',
                px: 1.5,
                py: 0.5,
                color: 'error.main',
                borderBottom: 1,
                borderColor: 'divider'
            }}
        >
            {intl.formatMessage({
                id: `translation.error.${fieldTranslation.error}`
            })}
        </Typography>
    );
}
